import { api } from "@/api"
import type { Ref } from "vue"
import { useQuery } from "@tanstack/vue-query"
import type {
  PaymentDetailCard,
  PaymentDetailCash,
  PaymentDetailSplit,
} from "../sharedTypes/invoices"
import type { CursorPaginationQuery } from "@/utils/utilityTypes"

export type TransactionsFeedFilters = {
  start?: string
  end?: string
  search?: string
  deviceId?: number
  cashierId?: string
}

export type TransactionsFeedCursor = {
  cursorAt?: string | null
  cursorPriority?: number | null
}

export type TransactionsFeedQuery =
  TransactionsFeedFilters &
    CursorPaginationQuery<TransactionsFeedCursor>

export type TransactionsFeedOrder = {
  id: string
  note: string | null
  numberOfItems: number
  createdAt: Date
  totalCost: number
  cashierId: string | null
  invoice: {
    id: string
    amountGross: number
    currency: string
    createdAt: Date
    isPaid: boolean
    paidBy:
      | PaymentDetailCard
      | PaymentDetailCash
      | PaymentDetailSplit
      | null
    paidAt: Date | null
  } | null
  orderNumber: number | null
}

export type TransactionsFeedZReport = {
  id: string
  createdAt: Date
  deviceId: number
  deviceSerialNumber: string
  fromReceiptNo: string | null
  toReceiptNo: string | null
  totalCount: number | null
  totalAmount: number | null
}

export type TransactionsFeedItem =
  | {
      kind: "order"
      order: TransactionsFeedOrder
      zReport: null
      createdAt: Date
    }
  | {
      kind: "z_report"
      order: null
      zReport: TransactionsFeedZReport
      createdAt: Date
    }

export type TransactionsFeedResponse = {
  items: TransactionsFeedItem[]
  hasNext: boolean
  nextCursorAt: string | null
  nextCursorPriority: number | null
}

type RawResponse =
  RecursivelyConvertDatesToStrings<TransactionsFeedResponse>

const listTransactionsFeed = async (
  query?: TransactionsFeedQuery
): Promise<TransactionsFeedResponse> => {
  const { body } = await api.get<RawResponse>(
    "/transactions-feed/",
    { query }
  )

  return {
    hasNext: body.hasNext,
    nextCursorPriority: body.nextCursorPriority,
    nextCursorAt: body.nextCursorAt,
    items: body.items.map((item) => {
      if (item.kind === "order" && item.order) {
        return {
          kind: "order" as const,
          zReport: null,
          createdAt: DateFns.parseISO(item.createdAt),
          order: {
            ...item.order,
            createdAt: DateFns.parseISO(
              item.order.createdAt
            ),
            invoice: item.order.invoice && {
              ...item.order.invoice,
              createdAt: DateFns.parseISO(
                item.order.invoice.createdAt
              ),
              paidAt: item.order.invoice.paidAt
                ? DateFns.parseISO(
                    item.order.invoice.paidAt
                  )
                : null,
            },
          },
        }
      }
      return {
        kind: "z_report" as const,
        order: null,
        createdAt: DateFns.parseISO(item.createdAt),
        zReport: item.zReport && {
          ...item.zReport,
          createdAt: DateFns.parseISO(
            item.zReport.createdAt
          ),
        },
      }
    }),
  }
}

export const useTransactionsFeed = (
  query: TransactionsFeedQuery | Ref<TransactionsFeedQuery>
) =>
  useQuery({
    queryKey: [
      BASE_TRANSACTIONS_FEED_QUERY_KEY,
      query,
    ] as const,
    queryFn: ({ queryKey: [_, query_] }) =>
      listTransactionsFeed(query_),
    refetchInterval: 60 * 1000,
    staleTime: 0,
  })
